import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Loader2, Inbox } from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminFeedback() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feedback");
      setItems(data.feedback || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not load feedback.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this feedback? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((f) => f.id !== id));
      toast.success("Feedback deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-clinical" data-testid="admin-feedback-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-8">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/" data-testid="admin-back-button">
              <ArrowLeft className="h-4 w-4" /> Back to dashboard
            </Link>
          </Button>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">
            Admin · Feedback
          </div>
        </div>

        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900">
            User feedback
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            Everything users have submitted through the in-app feedback button. Newest first.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500" data-testid="admin-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading feedback…
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-slate-300 bg-white/60">
            <CardContent className="p-10 text-center" data-testid="admin-empty-state">
              <Inbox className="h-8 w-8 mx-auto text-slate-400" />
              <p className="mt-3 font-heading text-lg text-slate-800">No feedback yet</p>
              <p className="mt-1 text-slate-500 text-sm">
                As soon as users submit feedback, it will show up here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="admin-feedback-list">
            <p className="text-sm text-slate-500">
              Showing <span className="font-semibold text-slate-700">{items.length}</span> submission{items.length === 1 ? "" : "s"}.
            </p>
            {items.map((f) => (
              <Card key={f.id} className="border-slate-200 bg-white" data-testid={`feedback-item-${f.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-heading text-base font-semibold text-slate-900">
                        {f.name || "Anonymous"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {f.email ? <a href={`mailto:${f.email}`} className="text-primary hover:underline">{f.email}</a> : "no email provided"}
                        {f.page ? <> · <span className="text-slate-400">{f.page}</span></> : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{formatDate(f.created_at)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(f.id)}
                        disabled={deletingId === f.id}
                        data-testid={`feedback-delete-${f.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                      >
                        {deletingId === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap text-slate-800 text-sm leading-relaxed">
                    {f.message}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
